import React, { Fragment, useMemo } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { ActionPanel } from "components/panel/ActionPanel";
import { GridLayout } from "components/grid";
import { ShowHide } from "components/layout";
import { NotificationInline } from "components/notifications";
import { ListingTable } from "components/listing";
import { Icon, Position } from "@blueprintjs/core";
import { Tooltip } from "components/tooltip";
import { InternalLink } from "components/navigation";

export function EventChecksPanel({
    check = null
}) {

    const indeterminateColour = "#008CFF";
    const failedColour = "#F0578A";
    const passedColour = "#33D1C1";

    let panelColour = indeterminateColour;
    if (check?.passFailResult?.passed === true) {
        panelColour = passedColour;
    } else if (check?.passFailResult?.passed === false) {
        panelColour = failedColour;
    }

    function showField(obj, fieldName) {
        return obj && obj[fieldName] !== null && obj[fieldName] !== undefined;
    }

    function formatDate(date) {
        if (!date) {
            return "";
        }
        return moment(date).format("DD/MM/YYYY");
    }

    function getDefectsFromCheck(checkObj) {
        if (!checkObj?.answers) return [];
        return checkObj.answers
            .flatMap(a =>
                a.answers?.filter(ans => ans.$answerType === "DefectCapture" && Array.isArray(ans.defects))
                    .flatMap(ans => ans.defects) || []
            );
    }

    const defects = getDefectsFromCheck(check);

    const showSignOffDate = defects.some(defect => !!defect.signOffDate);
    const showSignOffUser = defects.some(defect => !!defect.signOffUser);

    const defectTableHeaders = [
        "Comment",
        "Rectified",
        "Rectified By",
        ...(showSignOffDate ? ["Sign-off Date"] : []),
        ...(showSignOffUser ? ["Sign-off User"] : []),
        "" // Warning column
    ];

    const defectTableData = defects.map(defect => [
        defect.defectComment,
        defect.rectified ? "Yes" : "No",
        defect.rectifiedBy,
        ...(showSignOffDate ? [defect.signOffDate ? formatDate(defect.signOffDate) : ""] : []),
        ...(showSignOffUser ? [defect.signOffUser || ""] : []),
        (!defect.signOffUser && !defect.signOffDate) ? (
            <Tooltip key={`tooltip-warning-${defect.defectId}`} position={Position.TOP} content="This defect has not been signed off.">
                <Icon icon="warning-sign" intent="warning" />
            </Tooltip>
        ) : ""
    ]);

    const requireManagerSignOff = !!check?.requireManagerSignOff;
    const missingManagerSignOff =
        requireManagerSignOff &&
        (!showField(check?.managerSignOff, "signOffDate") ||
            !showField(check?.managerSignOff?.manager, "username"));

    const title = useMemo(() => {
        if (check?.id) {
            let templateName = check?.templateName ? `Related Check - ${check?.templateName}` : "Check"
            return (
                <InternalLink link={`/checks/${check.id}/details`}>
                    { templateName }
                </InternalLink>
            )
        }
        return (<>Check</>);
    }, [check]);

    return (
        <Fragment>
            <ActionPanel
                intent="info"
                colour={panelColour}
                title={title}
            >
                <NotificationInline
                    show={missingManagerSignOff}
                    text="This check has not been signed off by a manager."
                    intent="warning"
                    allowClose={false}
                />
                <GridLayout numberOfColumns={4}>
                    <ShowHide
                        evaluator={showField(check, "endDateTime")}
                        show={
                            <div className="form-field">
                                <h4 className="summary-field-header">Date Completed</h4>
                                <div>{formatDate(check?.endDateTime)}</div>
                            </div>
                        }
                    />
                    <ShowHide
                        evaluator={showField(check?.passFailResult, "passed")}
                        show={
                            <div className="form-field">
                                <h4 className="summary-field-header">Check Status</h4>
                                <div>{check?.passFailResult?.passed ? "Passed" : "Failed"}</div>
                            </div>
                        }
                    />
                    <ShowHide
                        evaluator={showField(check?.passFailResult, "comment")}
                        show={
                            <div className="form-field">
                                <h4 className="summary-field-header">Comment</h4>
                                <div>{check?.passFailResult?.comment}</div>
                            </div>
                        }
                    />
                    <ShowHide
                        evaluator={showField(check?.managerSignOff?.manager, "username")}
                        show={
                            <div className="form-field">
                                <h4 className="summary-field-header">Manager Sign-off User</h4>
                                <div>{check?.managerSignOff?.manager?.username}</div>
                            </div>
                        }
                    />
                    <ShowHide
                        evaluator={showField(check?.managerSignOff, "signOffDate")}
                        show={
                            <div className="form-field">
                                <h4 className="summary-field-header">Manager Sign-off Date</h4>
                                <div>{formatDate(check?.managerSignOff?.signOffDate)}</div>
                            </div>
                        }
                    />
                </GridLayout>
                {defects.length > 0 && (
                    <div>
                        <h4 className="summary-field-header">Defects</h4>
                        <ListingTable
                            id="listing-table-defects"
                            headers={defectTableHeaders}
                            data={defectTableData}
                            totalRecordCount={defectTableData.length}
                            pageable={false}
                            loadingData={false}
                        />
                    </div>
                )}
            </ActionPanel>
        </Fragment>
    );
}

EventChecksPanel.propTypes = {
    check: PropTypes.object
};